import { flatten } from 'utils';
import messagesInCanadianFrench from '../../lang/fr-CA.json';
import messagesInDutch from '../../lang/nl-NL.json';
import messagesInEnglish from '../../lang/en.json';
import messagesInFrench from '../../lang/fr-FR.json';
import messagesInGerman from '../../lang/de-DE.json';
import messagesInSpanish from '../../lang/es-ES.json';
import messagesInItalian from '../../lang/it-IT.json';
import messagesInNorwegian from '../../lang/nb-NO.json';
import messagesInPolish from '../../lang/pl.json';
import messagesInPortuguese from '../../lang/pt-PT.json';
import messagesInUnitedKingdomEnglish from '../../lang/en-GB.json';
import messagesInCanadianEnglish from '../../lang/en-CA.json';
import messagesInIrishEnglish from '../../lang/en-IE.json';
import messagesInAustralianEnglish from '../../lang/en-AU.json';
import messagesInHungarian from '../../lang/hu-HU.json';
import messagesInSlovak from '../../lang/sk-SK.json';
import { useMemo } from 'react';

export const DEFAULT_LOCALE = 'en';

const messagesMap = new Map<string, Record<string, unknown>>([
  ['en', messagesInEnglish.mfeWhoIsIn],
  ['es-es', messagesInSpanish.mfeWhoIsIn],
  ['fr-ca', messagesInCanadianFrench.mfeWhoIsIn],
  ['de-de', messagesInGerman.mfeWhoIsIn],
  ['nl-nl', messagesInDutch.mfeWhoIsIn],
  ['fr-fr', messagesInFrench.mfeWhoIsIn],
  ['it-it', messagesInItalian.mfeWhoIsIn],
  ['nb-no', messagesInNorwegian.mfeWhoIsIn],
  ['pl', messagesInPolish.mfeWhoIsIn],
  ['pt-pt', messagesInPortuguese.mfeWhoIsIn],
  ['en-gb', messagesInUnitedKingdomEnglish.mfeWhoIsIn],
  ['en-ca', messagesInCanadianEnglish.mfeWhoIsIn],
  ['en-ie', messagesInIrishEnglish.mfeWhoIsIn],
  ['en-au', messagesInAustralianEnglish.mfeWhoIsIn],
  ['hu-hu', messagesInHungarian.mfeWhoIsIn],
  ['sk-sk', messagesInSlovak.mfeWhoIsIn],
]);

/**
 * The catalogue that serves a region-less language. Every messagesMap key but `en` and `pl` carries a
 * region, so without this the language tier below could never fire: a bare `de`/`fr`/`pt`, or a region
 * we hold no catalogue for (`pt-BR`, `fr-BE`, `de-AT`), fell all the way through to English.
 */
const CATALOGUE_FOR_LANGUAGE = new Map<string, string>([
  ['de', 'de-de'],
  ['es', 'es-es'],
  ['fr', 'fr-fr'],
  ['hu', 'hu-hu'],
  ['it', 'it-it'],
  ['nb', 'nb-no'],
  ['nl', 'nl-nl'],
  ['pt', 'pt-pt'],
  ['sk', 'sk-sk'],
]);

/** The catalogue serving a tag, exact match first then its language, or undefined if we have none. */
const catalogueFor = (normalized: string): string | undefined => {
  if (messagesMap.has(normalized)) {
    return normalized;
  }

  const [language] = normalized.split('-');

  return messagesMap.has(language) ? language : CATALOGUE_FOR_LANGUAGE.get(language);
};

/**
 * Returns the catalogue key and the locale that formats dates. The two must never disagree on
 * LANGUAGE: a tag we hold no catalogue for renders English words, so its dates have to be English
 * too — otherwise the screen mixes English copy with a foreign date. A region we do not carry keeps
 * its own region for field order (`pt-BR` orders Brazilian) while borrowing `pt-PT`'s wording.
 */
export const getAvailableLocale = (userLocale: string): [string, string] => {
  // Case-fold for the lookup only: a host sends the BCP-47 canonical form, which capitalises the
  // region ('hu-HU'), while every map key is lower-case. The locale returned keeps the caller's case.
  const catalogue = catalogueFor(userLocale.toLowerCase());

  return catalogue ? [catalogue, userLocale] : [DEFAULT_LOCALE, DEFAULT_LOCALE];
};

interface LocaleData {
  messages: Record<string, string>;
  locale: string;
}

export const useLocaleData = (userLocale: string): LocaleData =>
  useMemo(() => {
    const [messagesKey, locale] = getAvailableLocale(userLocale);

    return {
      locale,
      messages: flatten(messagesMap.get(messagesKey)) as Record<string, string>,
    };
  }, [userLocale]);
